import { Link, useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { nextStatuses } from '../api/mappers';
import { useDeleteStore, useUpdateStore } from '../hooks/use-store-mutations';
import { activationBlockers } from '../schemas/store-rules';

/** Lifecycle buttons (activate, pause, resume, close) and delete for drafts, for a page header. */
export function StoreStatusActions({ store }: { store: Store }) {
  const { t } = useTranslation(['stores', 'common', 'errors']);
  const navigate = useNavigate();
  const update = useUpdateStore(store.id);
  const remove = useDeleteStore();
  const message = useErrorMessage();
  const error = isApiError(update.error)
    ? update.error
    : isApiError(remove.error)
      ? remove.error
      : null;

  const blockers = activationBlockers(update.error);
  const errorText = error
    ? blockers
      ? t('stores:status.notReady')
      : error.code === 'RESOURCE_IN_USE'
        ? t('stores:danger.notDraft')
        : error.code === 'CONFLICT' &&
            typeof error.meta.from === 'string' &&
            typeof error.meta.to === 'string'
          ? t('stores:status.transitionRefused', { from: error.meta.from, to: error.meta.to })
          : message(error)
    : null;

  return (
    <div className="flex flex-col gap-2 md:items-end">
      <div className="flex flex-wrap items-center gap-2 md:justify-end">
        {store.status === 'draft' ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={remove.isPending}
              >
                <Trash2 aria-hidden />
                {t('stores:danger.delete')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('stores:danger.title')}</AlertDialogTitle>
                <AlertDialogDescription>{t('stores:danger.description')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    remove.mutate(store.id, {
                      onSuccess: () => void navigate({ to: '/org/stores' }),
                    })
                  }
                >
                  {t('common:actions.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
        {nextStatuses(store.status).map((status) =>
          status === 'closed' ? (
            <AlertDialog key={status}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={update.isPending}
                >
                  {t('stores:status.close')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('stores:status.closeTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('stores:status.closeDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => update.mutate({ status: 'closed' })}>
                    {t('stores:status.close')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              key={status}
              size="sm"
              variant={status === 'active' ? 'default' : 'outline'}
              disabled={update.isPending}
              onClick={() => update.mutate({ status })}
            >
              {t(
                `stores:status.to.${store.status === 'paused' && status === 'active' ? 'resume' : status}`,
              )}
            </Button>
          ),
        )}
      </div>
      {errorText ? (
        <Alert variant="destructive" role="alert" className="md:max-w-sm">
          <AlertDescription>
            {errorText}
            {blockers && blockers.length > 0 ? (
              <ul
                className="mt-2 list-disc space-y-1 pl-4"
                aria-label={t('stores:status.blockers')}
              >
                {blockers.map((check) => (
                  <li key={check.key}>
                    <Link to={check.link} className="underline underline-offset-4">
                      {check.message}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            {blockers ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3"
                disabled={update.isPending}
                onClick={() => update.mutate({ status: 'active', force: true })}
              >
                {t('stores:status.activateAnyway')}
              </Button>
            ) : null}
            {error?.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
